/**
 * storefrontNavBarTheme -- the storefront category nav painted IN the store's
 * brand color.
 *
 * WHY THIS EXISTS (header inversion, T+L demo, Jake's call): the brand bar
 * used to be the brand color and the category nav white. A distributor's
 * uploaded logo is frequently an opaque JPEG with white baked into its pixels
 * and no alpha channel (T+L's real logo is exactly this -- CMYK, 2198x514,
 * ink running edge to edge), so an opaque white rectangle sat against the
 * brand color and read as a rendering defect. No CSS can make a JPEG's white
 * transparent. The fix inverts the two bands: the brand bar goes WHITE, which
 * turns the logo's baked-in white into the correct background, and the
 * CATEGORY NAV below it carries the brand color instead.
 *
 * THE PROBLEM THAT INVERSION CREATES: the nav links now sit on an arbitrary,
 * distributor-chosen background. `brand_color` is unconstrained -- it can be a
 * mid-dark teal (#236180, T+L's) or a near-white cream. A constant ink is
 * therefore wrong in one direction or the other: white links vanish on a pale
 * brand, dark links vanish on a navy one. This module decides the ink from the
 * bar's own luminance so BOTH stores get a legible nav.
 *
 * RELATIONSHIP TO THE TWO SIBLING MODULES -- all three share the WCAG math in
 * `utils/luminance` and the "clamp toward legibility, keep the hue" strategy:
 *
 *   - `storefrontNavInk.js` clamps a FOREGROUND (the active item's ink) against
 *     a FIXED white bar. Its `clampToContrast` already picks its scaling
 *     direction from the surface's luminance, which is exactly the primitive
 *     needed here, so this module composes it rather than re-deriving the math.
 *   - `storefrontBrandAccent.js` does the same for the Home page's welcome card
 *     against the light card surface.
 *   - THIS module inverts the relationship: the brand color is the BACKGROUND
 *     and the ink is derived from it.
 *
 * A background is used VERBATIM -- there is nothing to clamp about a bar the
 * store deliberately chose. All of the adaptation happens in the ink.
 */
import { hexToRgb, relativeLuminance } from "@/utils/luminance";
import {
  clampToContrast,
  TEXT_MIN_RATIO,
  NON_TEXT_MIN_RATIO,
} from "@/utils/storefrontNavInk";

/**
 * The bar rendered when a store has no usable brand color. This is the nav's
 * PRE-INVERSION appearance (white bar, dark links) -- deliberately chosen as
 * the fallback so a store with a null/malformed `brand_color` degrades to the
 * known-good nav rather than to a transparent or black bar.
 */
export const NAV_BAR_FALLBACK_SURFACE = "#ffffff";

// Ink candidates, tried in order against the bar. The first that clears its
// contrast floor wins; `clampToContrast` guarantees the terminal fallback is
// legible even if neither does.
const LIGHT_INK = "#ffffff";
const DARK_INK = "#1c1c1c";

// Resting (non-active) links are SECONDARY: legible, but visibly quieter than
// the active item so the active state still reads as the strongest thing in
// the bar. These are the resting inks -- pulled toward the bar rather than
// pushed to full white/near-black. They are still clamped to AA below, so a
// bar where this softening would cost legibility gets the full-strength ink
// back instead.
const LIGHT_INK_RESTING = "#e6edf2";
const DARK_INK_RESTING = "#3f4f5c";

// Luminance above which a bar counts as "light" and wants dark ink. This is
// the sRGB midpoint used by `clampToContrast` for the same decision, kept
// consistent so the two modules never disagree about which way is "away from
// the background".
const LIGHT_SURFACE_LUMINANCE = 0.5;

/**
 * Normalize whatever came out of `stores.brand_color` into a usable bar color.
 * Returns the fallback surface for null, non-strings, blank strings and
 * anything `hexToRgb` cannot parse -- so a corrupted column value degrades to
 * the known-good white nav instead of an unstyled or invisible one.
 */
function resolveBarColor(brandColor) {
  if (typeof brandColor !== "string") return NAV_BAR_FALLBACK_SURFACE;
  const trimmed = brandColor.trim();
  if (!trimmed) return NAV_BAR_FALLBACK_SURFACE;
  if (!hexToRgb(trimmed)) return NAV_BAR_FALLBACK_SURFACE;
  return trimmed;
}

/**
 * Pick the ink that contrasts AWAY from `barColor`, then clamp it so it
 * provably clears `minRatio`.
 *
 * The initial pick is the decision this module is FOR (light bar -> dark ink,
 * dark bar -> light ink); `clampToContrast` is the safety net that guarantees
 * the result actually clears the floor, including for mid-tone bars where the
 * first pick is marginal. Because clampToContrast scales AWAY from the surface
 * it can only strengthen the choice, never flip it.
 */
function inkFor(barColor, preferredLight, preferredDark, minRatio) {
  const barLuminance = relativeLuminance(barColor);
  const barIsLight =
    barLuminance === null || barLuminance > LIGHT_SURFACE_LUMINANCE;
  const preferred = barIsLight ? preferredDark : preferredLight;
  return (
    clampToContrast(preferred, barColor, minRatio) ||
    (barIsLight ? DARK_INK : LIGHT_INK)
  );
}

/**
 * Build the category nav's colors for a given store brand color.
 *
 * @param {string|null|undefined} brandColor  `stores.brand_color` (hex).
 * @returns {{
 *   background: string,       // the bar -- the brand color verbatim, or white
 *   link: string,             // resting link ink (secondary weight)
 *   linkHover: string,        // hover ink (full strength)
 *   linkActive: string,       // active link ink (full strength)
 *   activeUnderline: string,  // active underline, held to the non-text bar
 * }}
 *   Every ink is guaranteed legible against `background`: the text inks clear
 *   WCAG AA (4.5:1) and the underline clears 1.4.11 non-text (3:1). Callers
 *   bind this straight into a Vue `:style` object.
 */
export function storefrontNavBarTheme(brandColor) {
  const background = resolveBarColor(brandColor);

  // Full-strength inks for the states that need to read as primary.
  const strong = inkFor(background, LIGHT_INK, DARK_INK, TEXT_MIN_RATIO);

  return {
    background,
    // Resting links are quieter, but still clamped to AA -- on a bar where the
    // softened ink cannot clear the floor, clampToContrast pushes it back
    // toward full strength rather than shipping an illegible resting state.
    link: inkFor(
      background,
      LIGHT_INK_RESTING,
      DARK_INK_RESTING,
      TEXT_MIN_RATIO
    ),
    linkHover: strong,
    linkActive: strong,
    // WCAG 1.4.11: the underline is a graphical affordance, not text, so it
    // is held to 3:1 rather than 4.5:1 -- the same split storefrontNavInk
    // makes for the same element on the pre-inversion white bar.
    activeUnderline: inkFor(
      background,
      LIGHT_INK,
      DARK_INK,
      NON_TEXT_MIN_RATIO
    ),
  };
}

export default storefrontNavBarTheme;
